import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    CalendarDays,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Search,
    TrendingUp,
    X,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getPartnerGrowthReferrals } from "../../../../Redux/referral/slice";
import PartnerGrowthTable from "./PartnerGrowthTable";
import { PartnerGrowthItem } from "../../../../Redux/referral/types";
import "./PartnerGrowth.css";

const LIMIT_OPTIONS = [10, 25, 50, 75, 100];

const formatDateRange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) {
        return "Select Date Range";
    }
    const format = (value: string) =>
        new Date(value).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    return `${format(startDate)} to ${format(endDate)}`;
};

const PartnerGrowth = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { partnerGrowth } = useSelector((state: RootState) => state.referral);
    const { data, pagination, loading, error } = partnerGrowth;

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
    const [entriesOpen, setEntriesOpen] = useState(false);
    const [dateOpen, setDateOpen] = useState(false);
    const controlsRef = useRef<HTMLDivElement>(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        dispatch(
            getPartnerGrowthReferrals({
                page,
                limit,
                search: debouncedSearch || undefined,
                startDate: dateRange.startDate || undefined,
                endDate: dateRange.endDate || undefined,
            })
        );
    }, [dispatch, page, limit, debouncedSearch, dateRange]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                controlsRef.current &&
                !controlsRef.current.contains(event.target as Node)
            ) {
                setEntriesOpen(false);
                setDateOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
        setEntriesOpen(false);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const clearSearch = () => {
        setSearch("");
        setPage(1);
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setDateRange((prev) => ({ ...prev, [name]: value }));
        setPage(1);
    };

    const totalPages = pagination?.totalPages || 0;
    const totalResults = pagination?.total ?? data.length;
    const firstResult = data.length ? (page - 1) * limit + 1 : 0;
    const lastResult = data.length ? (page - 1) * limit + data.length : 0;
    const successCount = useMemo(
        () =>
            data.filter(
                (item: PartnerGrowthItem) =>
                    item.status?.toLowerCase() === "success"
            ).length,
        [data]
    );

    const rows = data.map((item: PartnerGrowthItem, index: number) => ({
        ...item,
        sNo: (page - 1) * limit + index + 1,
    }));

    return (
        <section className="partner-growth-page">
            <div className="partner-growth-shell">
                <div className="partner-growth-card">
                    <header className="partner-growth-header">
                        <div className="partner-growth-heading">
                            <span className="partner-growth-icon">
                                <TrendingUp size={19} strokeWidth={1.9} />
                            </span>

                            <div>
                                <span className="partner-growth-kicker">
                                    MY NETWORK
                                </span>
                                <h1>Partner Growth Referrals</h1>
                                <p>
                                    Track your partner growth referral entries and their status.
                                </p>
                            </div>
                        </div>

                        <div className="partner-growth-controls" ref={controlsRef}>
                            <div className="partner-growth-dropdown">
                                <button
                                    type="button"
                                    className={`partner-growth-filter-button ${entriesOpen ? "open" : ""}`}
                                    onClick={() => {
                                        setEntriesOpen((prev) => !prev);
                                        setDateOpen(false);
                                    }}
                                    aria-haspopup="listbox"
                                    aria-expanded={entriesOpen}
                                >
                                    <span>Last {limit} Entries</span>
                                    <ChevronDown size={14} strokeWidth={2} />
                                </button>
                                {entriesOpen && (
                                    <div
                                        className="partner-growth-dropdown-menu"
                                        role="listbox"
                                        aria-label="Entries per page"
                                    >
                                        {LIMIT_OPTIONS.map((value) => (
                                            <button
                                                type="button"
                                                key={value}
                                                className={`partner-growth-dropdown-item ${value === limit ? "active" : ""}`}
                                                onClick={() => handleLimitChange(value)}
                                            >
                                                Last {value} Entries
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="partner-growth-dropdown">
                                <button
                                    type="button"
                                    className={`partner-growth-filter-button ${dateOpen ? "open" : ""}`}
                                    onClick={() => {
                                        setDateOpen((prev) => !prev);
                                        setEntriesOpen(false);
                                    }}
                                    aria-haspopup="dialog"
                                    aria-expanded={dateOpen}
                                >
                                    <CalendarDays size={14} strokeWidth={1.9} />
                                    <span>
                                        Date Range:{" "}
                                        {formatDateRange(
                                            dateRange.startDate,
                                            dateRange.endDate
                                        )}
                                    </span>
                                    <ChevronDown size={14} strokeWidth={2} />
                                </button>
                                {dateOpen && (
                                    <div className="partner-growth-dropdown-menu partner-growth-date-menu">
                                        <label className="partner-growth-date-field">
                                            <span>Start Date</span>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={dateRange.startDate}
                                                onChange={handleDateChange}
                                            />
                                        </label>
                                        <label className="partner-growth-date-field">
                                            <span>End Date</span>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={dateRange.endDate}
                                                onChange={handleDateChange}
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>

                            <div className="partner-growth-search">
                                <Search size={16} strokeWidth={1.9} />
                                <input
                                    type="text"
                                    inputMode="search"
                                    value={search}
                                    onChange={handleSearchChange}
                                    placeholder="Search Growth ID..."
                                    aria-label="Search partner growth referrals"
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={clearSearch}
                                        aria-label="Clear search"
                                    >
                                        <X size={14} strokeWidth={2.2} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </header>

                    <div className="partner-growth-summary-row">
                        <div className="partner-growth-summary">
                            <span>Total Referrals</span>
                            <strong>{totalResults}</strong>
                        </div>
                        <div className="partner-growth-summary">
                            <span>Successful</span>
                            <strong>{successCount}</strong>
                        </div>
                    </div>

                    <div className="partner-growth-table-shell">
                        <div
                            className="partner-growth-table-scroll"
                            role="region"
                            aria-label="Partner growth referrals table"
                            tabIndex={0}
                        >
                            <PartnerGrowthTable
                                data={rows}
                                loading={loading}
                                error={error}
                            />
                        </div>
                    </div>

                    <footer className="partner-growth-footer">
                        <span className="partner-growth-result-count">
                            Showing <strong>{firstResult}</strong> -{" "}
                            <strong>{lastResult}</strong> of{" "}
                            <strong>{totalResults}</strong>
                        </span>

                        <div className="partner-growth-pagination">
                            <button
                                type="button"
                                className="partner-growth-pagination-arrow"
                                disabled={page === 1}
                                onClick={() => handlePageChange(page - 1)}
                                aria-label="Previous page"
                            >
                                <ChevronLeft size={16} strokeWidth={2} />
                            </button>

                            {Array.from(
                                { length: totalPages },
                                (_, index) => index + 1
                            ).map((pageNumber) => (
                                <button
                                    type="button"
                                    className={`partner-growth-pagination-page ${page === pageNumber ? "active" : ""}`}
                                    key={pageNumber}
                                    onClick={() => handlePageChange(pageNumber)}
                                    aria-current={
                                        page === pageNumber ? "page" : undefined
                                    }
                                >
                                    {pageNumber}
                                </button>
                            ))}

                            <button
                                type="button"
                                className="partner-growth-pagination-arrow"
                                disabled={page >= totalPages || totalPages === 0}
                                onClick={() => handlePageChange(page + 1)}
                                aria-label="Next page"
                            >
                                <ChevronRight size={16} strokeWidth={2} />
                            </button>
                        </div>
                    </footer>
                </div>
            </div>
        </section>
    );
};

export default PartnerGrowth;
